import { Toaster as Sonner } from 'sonner';
import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsEmbedded } from '@/hooks/embed/useEmbedMode';

type ToasterProps = React.ComponentProps<typeof Sonner>;

// White icons that sit inside the coloured severity band (see the [data-sonner-toast] rules
// in index.css). Follows the references' toast convention: success = circle-check,
// info = info, warning + error = triangle-alert.
const toastIcons = {
  success: <CircleCheck className="size-6 text-white" />,
  info: <Info className="size-6 text-white" />,
  warning: <TriangleAlert className="size-6 text-white" />,
  error: <TriangleAlert className="size-6 text-white" />,
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();
  // Validate's own toaster also renders bottom-left; inside the frame the two stacks would
  // overlap at the same corner, so move Extract's to the opposite side (DEV embed, §7 D6).
  const isEmbedded = useIsEmbedded();

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      icons={toastIcons}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-card group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:min-w-[500px] group-[.toaster]:max-w-[600px] group-[.toaster]:relative group-[.toaster]:pr-8',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-transparent group-[.toast]:text-foreground group-[.toast]:hover:bg-black/6 dark:group-[.toast]:hover:bg-white/10',
          cancelButton:
            'group-[.toast]:bg-transparent group-[.toast]:text-muted-foreground group-[.toast]:hover:bg-black/6 dark:group-[.toast]:hover:bg-white/10',
          closeButton:
            'group-[.toast]:absolute group-[.toast]:right-2 group-[.toast]:top-2 group-[.toast]:rounded-md group-[.toast]:p-1 group-[.toast]:text-foreground/50 group-[.toast]:opacity-100 group-[.toast]:hover:text-foreground group-[.toast]:focus:outline-none group-[.toast]:focus:ring-2 group-[.toast]:left-auto group-[.toast]:transform-none group-[.toast]:border-none group-[.toast]:bg-transparent',
        },
      }}
      position={isEmbedded ? 'bottom-right' : 'bottom-left'}
      duration={5000}
      visibleToasts={3}
      closeButton
      {...props}
    />
  );
};

export { Toaster };
